"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataGrid = DataGrid;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const list_pager_1 = require("./list-pager");
const skeleton_1 = require("./skeleton");
const tooltip_1 = require("./tooltip");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Bulk editing. Spreadsheet muscle memory inside the app.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Arrow keys move the cell, Enter or F2 edits, typing a character starts an edit with it,
 * Escape cancels, Ctrl/⌘+C copies, Ctrl/⌘+V pastes, Ctrl/⌘+D fills down from the row above.
 *
 * ⚠️ **BLUR COMMITS HERE, and that is a considered divergence from `state-rules.md` §8.** That
 * rule says *"blur does not save — blur keeps the editor open"*, and it is right about the
 * surface it was written for: a single inline editor on a detail pane, where an accidental click
 * elsewhere must not write. A grid cannot honour it. Focus moves between cells constantly, so
 * "keep the editor open" means an editor stranded on a cell the user has left — two apparent
 * cursors, and a later Enter commits a draft the user has forgotten about. The alternatives are
 * commit or discard, and discarding a value somebody typed is the worse failure.
 *
 * So: **§8 governs `InlineEdit`-style field editing; a grid cell commits on blur.** Flagged
 * rather than assumed, and `docs/plan/design-system-findings.md` carries the row. If the product
 * owner wants §8 applied literally here, the grid needs an explicit commit affordance per cell
 * and that is a different component.
 *
 * ⚠️ **Copy/paste is grid-internal, and `onCopy` is the escape hatch.** The clipboard held here
 * is a single string in a ref — pasting from Excel does nothing, because reading the system
 * clipboard needs `navigator.clipboard.readText()` and a permission prompt. That is the mockup's
 * behaviour too. `onCopy` lets a caller mirror the value out; there is deliberately no
 * `onPaste`, because a component that silently pastes something other than what it copied is
 * worse than one that only round-trips itself.
 *
 * ⚠️ **`required` is ENFORCED, which the source declares and does not do.** Its `.d.ts` says an
 * empty value is rejected via `onBlocked`; its implementation never checks. A column marked
 * required that silently accepts blanks is worse than no marking, because the asterisk is a
 * promise.
 */
function DataGrid({ columns, rows, labels, onChange, onCopy, onBlocked, page, isLoading = false, className, }) {
    /* ⚠️ `useId`, so two grids on one page cannot share the listbox id a cell points its
     * `aria-controls` at — a duplicate id makes assistive technology follow whichever it found
     * first. */
    const gridId = (0, react_1.useId)();
    const [cursor, setCursor] = (0, react_1.useState)({ row: 0, column: 0 });
    const [editing, setEditing] = (0, react_1.useState)(null);
    const clipboard = (0, react_1.useRef)('');
    const wrapper = (0, react_1.useRef)(null);
    const cellText = (row, column) => {
        const key = columns[column]?.key;
        if (key === undefined)
            return '';
        const value = rows[row]?.[key];
        return value === null || value === undefined ? '' : String(value);
    };
    /* ⚠️ `Cursor & { draft }` rather than `Editing`: paste and fill-down commit a value at a cell
     * NOBODY IS EDITING, so they have no highlight and no typing history to supply. */
    const commit = (at) => {
        const column = columns[at.column];
        const row = rows[at.row];
        if (column === undefined || row === undefined) {
            setEditing(null);
            return;
        }
        /* The enforcement the source promises and skips. Refuse, say why, and keep the editor open
         * so the value is not lost — a rejected edit that also discards the typing is two failures. */
        if (column.required === true && at.draft.trim() === '') {
            onBlocked?.(labels.required(column.label));
            return;
        }
        onChange?.(row.id, column.key, at.draft);
        setEditing(null);
    };
    const startEdit = (at, seed) => {
        const column = columns[at.column];
        if (column === undefined)
            return;
        if (column.readOnly === true) {
            onBlocked?.(labels.readOnly(column.label));
            return;
        }
        setEditing({
            ...at,
            draft: seed ?? cellText(at.row, at.column),
            highlight: -1,
            touched: seed !== undefined,
        });
    };
    const move = (rowDelta, columnDelta) => {
        setCursor((at) => ({
            row: Math.max(0, Math.min(rows.length - 1, at.row + rowDelta)),
            column: Math.max(0, Math.min(columns.length - 1, at.column + columnDelta)),
        }));
    };
    /**
     * The options this editor is offering, in order.
     *
     * ⚠⚠️ **AN IN-APP LISTBOX, NOT `datalist` — and the swap is the whole point.** The native
     * one renders in an OS-drawn window: system font, system colours, its own row height, ignoring
     * every token in this design system. In a themed app it reads as another program's popup, which
     * is what was reported. The behaviour it gave away for free — filter as you type, arrow to open,
     * Enter to take — is rebuilt below, because that part of it was right.
     */
    const optionsFor = (at) => {
        const all = columns[at.column]?.options;
        if (all === undefined)
            return [];
        if (!at.touched)
            return all;
        const needle = at.draft.trim().toLocaleLowerCase();
        if (needle === '')
            return all;
        return all.filter((option) => option.toLocaleLowerCase().includes(needle));
    };
    const onKeyDown = (event) => {
        if (editing !== null) {
            const options = optionsFor(editing);
            const chosen = options[editing.highlight];
            /* ⚠️ The list owns the arrows while it is open — without this they move the CELL and
               leave an editor open over a cell nobody is on any more. */
            if (options.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                const next = editing.highlight + step;
                setEditing({
                    ...editing,
                    /* Wraps, and -1 is skipped on the way round: once the list is being walked, every
                       stop on it is a real option. */
                    highlight: next < 0 ? options.length - 1 : next >= options.length ? 0 : next,
                });
                return;
            }
            if (chosen !== undefined && (event.key === 'Enter' || event.key === 'Tab')) {
                event.preventDefault();
                commit({ ...editing, draft: chosen });
                move(event.key === 'Enter' ? 1 : 0, event.key === 'Tab' ? (event.shiftKey ? -1 : 1) : 0);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setEditing(null);
            }
            else if (event.key === 'Enter') {
                event.preventDefault();
                commit(editing);
                move(1, 0);
            }
            else if (event.key === 'Tab') {
                event.preventDefault();
                commit(editing);
                move(0, event.shiftKey ? -1 : 1);
            }
            return;
        }
        const chord = event.ctrlKey || event.metaKey;
        const column = columns[cursor.column];
        switch (true) {
            case event.key === 'ArrowDown':
                event.preventDefault();
                move(1, 0);
                break;
            case event.key === 'ArrowUp':
                event.preventDefault();
                move(-1, 0);
                break;
            case event.key === 'ArrowRight':
                event.preventDefault();
                move(0, 1);
                break;
            case event.key === 'ArrowLeft':
                event.preventDefault();
                move(0, -1);
                break;
            case event.key === 'Tab':
                event.preventDefault();
                move(0, event.shiftKey ? -1 : 1);
                break;
            case event.key === 'Enter' || event.key === 'F2':
                event.preventDefault();
                startEdit(cursor);
                break;
            case chord && event.key.toLowerCase() === 'c':
                clipboard.current = cellText(cursor.row, cursor.column);
                onCopy?.(clipboard.current);
                break;
            case chord && event.key.toLowerCase() === 'v':
                event.preventDefault();
                if (column?.readOnly === true)
                    onBlocked?.(labels.readOnly(column.label));
                else
                    commit({ ...cursor, draft: clipboard.current });
                break;
            case chord && event.key.toLowerCase() === 'd':
                event.preventDefault();
                if (cursor.row === 0)
                    break;
                if (column?.readOnly === true)
                    onBlocked?.(labels.readOnly(column.label));
                else
                    commit({ ...cursor, draft: cellText(cursor.row - 1, cursor.column) });
                break;
            /* ⚠️ Type-to-edit, and the guards are load-bearing. `key.length === 1` alone also
             * matches Space — which should not start an edit — and every modifier chord. `Dead` and
             * IME composition keys are longer than one character so they fall through, which is
             * correct: starting an edit mid-composition eats the accented character. */
            case !chord && !event.altKey && event.key.length === 1 && event.key !== ' ':
                /* ⚠️ **`preventDefault` or the first character lands TWICE.** The keydown opens the
                 * editor seeded with the character; the input then mounts with `autoFocus`, and the same
                 * physical keypress's default action inserts the character again into the now-focused
                 * field. Typing `9` gives `99`. The source has this bug — it is invisible unless you
                 * type into a grid and read what came out. */
                event.preventDefault();
                startEdit(cursor, event.key);
                break;
            default:
                break;
        }
    };
    return (
    /**
     * ⚠️⚠️ **A COLUMN WRAPPER SINCE ITEMS 32/43, AND THE SCROLL MOVED INSIDE IT.** The grid used to
     * BE the scroll region; a footer added to that element would have scrolled away with the rows,
     * which is the one place a pager must never be. The outer box owns the height now and the
     * table's own box owns the overflow — the same two-element shape `DataList` uses, and for the
     * same reason its `h-full` note records at length.
     */
    (0, jsx_runtime_1.jsxs)("div", { className: (0, cn_1.cn)('flex min-h-0 flex-1 flex-col', className), children: [(0, jsx_runtime_1.jsxs)("div", { ref: wrapper, tabIndex: 0, onKeyDown: onKeyDown, role: "grid", "aria-label": labels.grid, "aria-rowcount": rows.length + 1, "aria-colcount": columns.length, className: "min-h-0 flex-1 overflow-auto outline-none", children: [(0, jsx_runtime_1.jsxs)("table", { className: "border-separate border-spacing-0 text-[12.5px]", children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: columns.map((column, index) => ((0, jsx_runtime_1.jsxs)("th", { scope: "col", style: {
                                            width: column.width,
                                            minWidth: column.width,
                                            position: 'sticky',
                                            left: index === 0 ? 0 : undefined,
                                        }, className: (0, cn_1.cn)(
                                        /* ⚠️⚠️ **OPAQUE, BECAUSE IT IS STICKY.** At `bg-muted/60` the rows scrolling
                                           underneath showed through the header that is pinned over them — reported as
                                           *"a sticky header egybemosódik a cellákkal a transparent háttér miatt"*. A
                                           translucent header is only ever legible over what happens to be behind it. */
                                        'bg-muted text-muted-foreground sticky top-0 z-20 h-8 border-r border-b px-2 text-left text-[11px] font-medium tracking-wide whitespace-nowrap uppercase', 
                                        /* The first column is frozen, so it needs to win against both the sticky
                                         * header and the scrolling body. */
                                        index === 0 && 'left-0 z-30'), children: [column.label, column.required === true ? (0, jsx_runtime_1.jsx)("span", { className: "text-destructive", children: " *" }) : null] }, column.key))) }) }), (0, jsx_runtime_1.jsxs)("tbody", { children: [!isLoading
                                        ? null
                                        : skeleton_1.SKELETON_WIDTHS.map((_widths, index) => ((0, jsx_runtime_1.jsx)("tr", { "aria-hidden": true, children: columns.map((column, at) => ((0, jsx_runtime_1.jsx)("td", { style: { width: column.width, minWidth: column.width }, className: "bg-background h-[30px] border-r border-b px-2", children: (0, jsx_runtime_1.jsx)(skeleton_1.Skeleton, { width: (0, skeleton_1.skeletonWidth)(index, at) }) }, column.key))) }, `skeleton-${String(index)}`))), (isLoading ? [] : rows).map((row, rowIndex) => ((0, jsx_runtime_1.jsx)("tr", { children: columns.map((column, columnIndex) => {
                                            const active = cursor.row === rowIndex && cursor.column === columnIndex;
                                            const open = editing !== null && editing.row === rowIndex && editing.column === columnIndex;
                                            const text = cellText(rowIndex, columnIndex);
                                            /* ⚠️ The full value only when the cell's ellipsis has cut it, never while it is
                                               being edited, and never the browser's `title` (20 Oct 2026). On the `<td>`
                                               itself — a wrapper inside a table row is not an option. */
                                            return ((0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, overflowOnly: true, content: open || text === '' ? undefined : text, children: (0, jsx_runtime_1.jsx)("td", { role: "gridcell", "aria-selected": active, "aria-readonly": column.readOnly, onClick: (event) => {
                                                        /* ⚠️ **Do not steal focus back from an open editor.** The source focuses
                                                         * the wrapper on every cell click, including a click INSIDE the input it
                                                         * just opened — so placing the caret in a half-typed value blurs the
                                                         * editor, commits whatever is there, and the next keystrokes go to the
                                                         * grid instead of the field. Clicking to position the cursor in a cell you
                                                         * are editing is the most ordinary thing to do in a spreadsheet. */
                                                        if (event.target instanceof HTMLInputElement)
                                                            return;
                                                        setCursor({ row: rowIndex, column: columnIndex });
                                                        wrapper.current?.focus();
                                                    }, onDoubleClick: () => {
                                                        setCursor({ row: rowIndex, column: columnIndex });
                                                        startEdit({ row: rowIndex, column: columnIndex });
                                                    }, className: (0, cn_1.cn)('relative h-[30px] max-w-[300px] border-r border-b px-2 text-ellipsis whitespace-nowrap', 
                                                    /* ⚠⚠️ **`overflow-hidden` CLIPS THE DROPDOWN TO 30 PIXELS.** It is here to
                                                   ellipsis long text, which the cell being EDITED does not show — the input
                                                   covers it — so the editing cell drops it and every other cell keeps it. */
                                                    open ? 'overflow-visible' : 'overflow-hidden', 
                                                    /* ⚠️ 17 Oct 2026 · OPAQUE: the frozen first column is read-only, and a 40%
                                                     tint let the columns scrolling under it show through (the key column
                                                     overlapping the project's name on a phone). */
                                                    column.readOnly === true
                                                        ? 'bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))] text-muted-foreground'
                                                        : 'bg-background', column.mono === true && 'tabular-nums', 
                                                    /* The one inner shadow in the product: the violet inset ring on a
                                                     * selected grid cell. */
                                                    active && 'ring-primary bg-primary/5 z-10 ring-2 ring-inset', row.invalid === column.key &&
                                                        'bg-destructive/10 ring-destructive ring-1 ring-inset'), style: {
                                                        width: column.width,
                                                        position: columnIndex === 0 ? 'sticky' : undefined,
                                                        left: columnIndex === 0 ? 0 : undefined,
                                                        zIndex: columnIndex === 0 ? (active ? 12 : 5) : active ? 10 : undefined,
                                                    }, children: open ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("input", { autoFocus: true, value: editing.draft, role: column.options === undefined ? undefined : 'combobox', "aria-expanded": column.options === undefined
                                                                    ? undefined
                                                                    : optionsFor(editing).length > 0, "aria-controls": column.options === undefined ? undefined : `${gridId}-options`, "aria-autocomplete": column.options === undefined ? undefined : 'list', onChange: (event) => {
                                                                    setEditing({
                                                                        ...editing,
                                                                        draft: event.target.value,
                                                                        /* ⚠️ Typing resets the highlight. Leaving it where it was points at
                                                                       whatever is now in that position of a DIFFERENT list. */
                                                                        highlight: -1,
                                                                        touched: true,
                                                                    });
                                                                }, 
                                                                /* Blur commits — see the note on the component. */
                                                                onBlur: () => {
                                                                    commit(editing);
                                                                }, "aria-label": column.label, className: "ring-primary bg-background absolute inset-0 h-full w-full px-2 font-[inherit] ring-2 ring-inset outline-none" }), column.options === undefined ? null : ((0, jsx_runtime_1.jsx)(GridOptions, { id: `${gridId}-options`, options: optionsFor(editing), highlight: editing.highlight, onPick: (option) => {
                                                                    commit({ ...editing, draft: option });
                                                                } }))] })) : text === '' ? (
                                                    /* An em-dash for absent, per `state-rules.md` §5. A grid of blanks cannot
                                                     * be told from a grid that failed to load. */
                                                    (0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground/50", children: "\u2013" })) : (text) }) }, column.key));
                                        }) }, row.id)))] })] }), !isLoading ? null : ((0, jsx_runtime_1.jsx)("div", { role: "status", "aria-live": "polite", className: "sr-only", children: labels.loading ?? '' }))] }), page === undefined || isLoading ? null : (
            /* ⚠️ The same strip the list draws, so the two footers cannot drift apart. */
            (0, jsx_runtime_1.jsxs)("div", { className: "bg-muted/30 text-muted-foreground flex shrink-0 flex-wrap items-center gap-2 border-t px-4 py-2 text-[11.5px]", children: [(0, jsx_runtime_1.jsx)("div", { className: "flex-1" }), (0, jsx_runtime_1.jsx)(list_pager_1.ListPager, { page: page, labels: labels })] }))] }));
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The cell's dropdown. In the app's own colours, not the operating system's. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`onMouseDown` WITH `preventDefault`, NOT `onClick` — AND THE ORDER IS WHY.** The input
 * above commits on blur. A plain click blurs the input first, which commits whatever was typed and
 * unmounts this list, so the click lands on nothing and the option is never taken. Suppressing the
 * mousedown's default keeps focus where it is until the pick has been made.
 *
 * ⚠️ **It hangs below the cell and is clipped by the grid's own scroller**, which is the trade for
 * not portalling: a row at the very bottom shows a short list rather than one that escapes the
 * table. `max-h` keeps that from being most rows, and the list scrolls itself.
 */
function GridOptions({ id, options, highlight, onPick, }) {
    if (options.length === 0)
        return null;
    return ((0, jsx_runtime_1.jsx)("ul", { id: id, role: "listbox", className: "bg-popover text-popover-foreground absolute top-full left-0 z-30 mt-0.5 max-h-[220px] min-w-full overflow-y-auto rounded-md border p-1 shadow-md", children: options.map((option, index) => ((0, jsx_runtime_1.jsx)("li", { role: "presentation", children: (0, jsx_runtime_1.jsx)("button", { type: "button", role: "option", "aria-selected": index === highlight, className: (0, cn_1.cn)('flex w-full items-center rounded-sm px-2 py-1 text-left text-[12.5px] whitespace-nowrap', index === highlight ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'), onMouseDown: (event) => {
                    event.preventDefault();
                    onPick(option);
                }, children: option }) }, option))) }));
}
//# sourceMappingURL=data-grid.js.map